import { useEffect, useMemo, useRef, useState } from 'react';
import type { Attachment, Conversation, Message } from '../../shared/types';
import { get, post } from '../api';
import { useCall } from '../hooks/useCall';
import { useChat } from '../state';
import { CallPanel } from './CallPanel';
import {
  IconBack,
  IconCheck,
  IconPhone,
  IconPhoneOff,
  IconReply,
  IconSend,
  IconSparkles,
  IconVideo,
} from './icons';
import { Avatar, PLATFORM_LABEL, dayLabel, formatBytes, formatClock } from './ui';

function Attachments({ attachments }: { attachments: Attachment[] }) {
  return (
    <div className="attachments">
      {attachments.map((attachment) => {
        if (attachment.kind === 'image') {
          return (
            <a key={attachment.id} href={attachment.url} target="_blank" rel="noreferrer" className="att-image">
              <img src={attachment.url} alt={attachment.name ?? 'image'} loading="lazy" />
            </a>
          );
        }
        if (attachment.kind === 'video') {
          return <video key={attachment.id} className="att-video" src={attachment.url} controls preload="metadata" />;
        }
        if (attachment.kind === 'voice' || attachment.kind === 'audio') {
          return <audio key={attachment.id} className="att-audio" src={attachment.url} controls preload="metadata" />;
        }
        return (
          <a key={attachment.id} className="att-file" href={attachment.url} target="_blank" rel="noreferrer">
            <span className="att-file-name">{attachment.name ?? 'File'}</span>
            <span className="att-file-meta">{formatBytes(attachment.size)}</span>
          </a>
        );
      })}
    </div>
  );
}

function MessageRow({ message, grouped, onReply }: { message: Message; grouped: boolean; onReply: (message: Message) => void }) {
  const self = Boolean(message.author.self);
  return (
    <div className={`msg-row ${self ? 'self' : 'other'} ${grouped ? 'grouped' : ''}`}>
      {!self ? (
        <div className="msg-avatar">{grouped ? null : <Avatar name={message.author.name} url={message.author.avatar} size={32} />}</div>
      ) : null}
      <div className={`bubble ${message.status === 'failed' ? 'failed' : ''}`}>
        {!self && !grouped ? <div className="bubble-author">{message.author.name}</div> : null}
        {message.replyPreview ? (
          <div className="reply-quote">
            <span>{message.replyPreview}</span>
          </div>
        ) : null}
        {message.attachments?.length ? <Attachments attachments={message.attachments} /> : null}
        {message.text ? (
          <div className="bubble-text">
            {message.text}
            {message.streaming ? <span className="caret" /> : null}
          </div>
        ) : null}
        <div className="bubble-meta">
          {message.via === 'ai' ? <span className="ai-tag">AI</span> : null}
          <span>{formatClock(message.ts)}</span>
          {message.editedAt ? <span className="edited">edited</span> : null}
          {self && message.status === 'sending' ? <span className="pending" /> : null}
          {self && message.status === 'sent' ? <IconCheck size={12} /> : null}
          {message.status === 'failed' ? <span className="failed-tag">failed</span> : null}
        </div>
        {message.error ? <div className="bubble-error">{message.error}</div> : null}
      </div>
      <button className="reply-btn" onClick={() => onReply(message)} title="Reply" aria-label="Reply">
        <IconReply size={14} />
      </button>
    </div>
  );
}

export function ChatView({ conversation }: { conversation: Conversation }) {
  const messages = useChat((state) => state.messages[conversation.id] ?? EMPTY);
  const setMessages = useChat((state) => state.setMessages);
  const aiMode = useChat((state) => state.aiMode);
  const toggleAiMode = useChat((state) => state.toggleAiMode);
  const notify = useChat((state) => state.notify);
  const capabilities = useChat((state) => state.capabilities);
  const setActive = useChat((state) => state.setActive);
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [showCall, setShowCall] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const stickRef = useRef(true);
  const call = useCall(conversation.id);

  useEffect(() => {
    let cancelled = false;
    get<{ messages: Message[] }>(`/api/conversations/${encodeURIComponent(conversation.id)}/messages`)
      .then((result) => {
        if (!cancelled) setMessages(conversation.id, result.messages);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [conversation.id, setMessages]);

  const lastText = messages.length ? messages[messages.length - 1].text : '';
  useEffect(() => {
    const element = listRef.current;
    if (!element) return;
    if (stickRef.current) element.scrollTop = element.scrollHeight;
  }, [messages.length, lastText, conversation.id]);

  const onScroll = () => {
    const element = listRef.current;
    if (!element) return;
    stickRef.current = element.scrollHeight - element.scrollTop - element.clientHeight < 90;
  };

  const grouped = useMemo(() => {
    return messages.map((message, index) => {
      const previous = messages[index - 1];
      return Boolean(
        previous &&
          previous.author.id === message.author.id &&
          message.ts - previous.ts < 5 * 60 * 1000 &&
          !previous.replyPreview,
      );
    });
  }, [messages]);

  const send = async () => {
    const raw = draft.trim();
    if (!raw) return;
    const isCommand = /^\/ai\s+/i.test(raw);
    const text = isCommand ? raw.replace(/^\/ai\s+/i, '').trim() : raw;
    if (!text) return;
    const target = replyTo;
    setDraft('');
    setReplyTo(null);
    stickRef.current = true;
    try {
      await post(`/api/conversations/${encodeURIComponent(conversation.id)}/messages`, {
        text,
        replyToId: target?.id,
      });
    } catch (error) {
      notify((error as Error).message, 'error');
    }
    if (isCommand || aiMode) {
      if (!capabilities?.ai.enabled) {
        notify('Add an AI API key in Settings to use the assistant', 'error');
        return;
      }
      try {
        if (call.connected) await call.ask(text);
        else await post(`/api/conversations/${encodeURIComponent(conversation.id)}/ai`, { prompt: text });
      } catch (error) {
        notify((error as Error).message, 'error');
      }
    }
  };

  const startCall = async (withVideo: boolean) => {
    if (!capabilities?.livekit.enabled) {
      notify('Add LiveKit credentials in Settings to start a call', 'error');
      return;
    }
    setShowCall(true);
    await call.join();
    if (withVideo) await call.toggleCamera();
  };

  const hangUp = async () => {
    await call.leave();
    setShowCall(false);
  };

  const copyInvite = async () => {
    if (!conversation.invite) return;
    try {
      await navigator.clipboard.writeText(conversation.invite);
      notify('Mesh invite copied');
    } catch {
      notify(conversation.invite);
    }
  };

  const subtitle = [
    PLATFORM_LABEL[conversation.platform],
    conversation.kind === 'dm' ? 'direct' : conversation.kind,
    conversation.platform === 'mesh' && conversation.peers ? `${conversation.peers} peers` : null,
    conversation.meta?.guild,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <section className="chat">
      <header className="chat-head">
        <button className="icon-btn back" onClick={() => setActive(null)} aria-label="Back">
          <IconBack />
        </button>
        <Avatar name={conversation.title} url={conversation.avatar} size={40} />
        <div className="chat-title">
          <strong>{conversation.title}</strong>
          <span>
            {subtitle}
            {conversation.invite ? (
              <button className="invite" onClick={copyInvite} title="Copy invite">
                {conversation.invite.slice(0, 8)}…
              </button>
            ) : null}
          </span>
        </div>
        <div className="chat-actions">
          {call.status === 'connected' ? (
            <button className="icon-btn danger" onClick={hangUp} title="Leave call">
              <IconPhoneOff />
            </button>
          ) : (
            <>
              <button className="icon-btn" onClick={() => void startCall(false)} title="Voice call">
                <IconPhone />
              </button>
              <button className="icon-btn" onClick={() => void startCall(true)} title="Video call">
                <IconVideo />
              </button>
            </>
          )}
        </div>
      </header>

      {showCall || call.status !== 'idle' ? (
        <CallPanel call={call} conversation={conversation} onClose={hangUp} />
      ) : null}

      <div className="messages" ref={listRef} onScroll={onScroll}>
        {messages.map((message, index) => {
          const previous = messages[index - 1];
          const showDay = !previous || new Date(previous.ts).toDateString() !== new Date(message.ts).toDateString();
          return (
            <div key={message.id}>
              {showDay ? <div className="day">{dayLabel(message.ts)}</div> : null}
              <MessageRow message={message} grouped={grouped[index]} onReply={setReplyTo} />
            </div>
          );
        })}
        {!messages.length ? <div className="empty-chat">No messages yet. Say hello.</div> : null}
      </div>

      {replyTo ? (
        <div className="reply-bar">
          <IconReply size={16} />
          <div>
            <strong>{replyTo.author.name}</strong>
            <span>{replyTo.text.slice(0, 90)}</span>
          </div>
          <button className="icon-btn" onClick={() => setReplyTo(null)} aria-label="Cancel reply">
            ×
          </button>
        </div>
      ) : null}

      <footer className="composer">
        <button
          className={`ai-toggle ${aiMode ? 'on' : ''}`}
          onClick={toggleAiMode}
          title="AI replies to what you send"
        >
          <IconSparkles size={18} />
          <span>AI</span>
        </button>
        <textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void send();
            }
          }}
          placeholder={aiMode ? 'Message · AI will answer' : 'Message'}
          rows={1}
        />
        <button className="send" onClick={() => void send()} disabled={!draft.trim()} aria-label="Send">
          <IconSend />
        </button>
      </footer>
    </section>
  );
}

const EMPTY: Message[] = [];
